import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SERVIZI_CATEGORIE, EROGATORI, CATEGORIE_SERVIZIO } from "@/data/siteData";
import { SITE_INFO } from "@/data/siteData";
import { Shield, Briefcase, Building2, Check, ArrowRight, Phone, Rocket, Scale, GraduationCap, Search, Laptop, AlertTriangle, ShieldCheck, Gift, Download } from "lucide-react";

const ICON_MAP = { Shield, Briefcase, Building2 };

const SERVIZI_LINKS = {
  "Consulenza sul lavoro e welfare": "/servizi/consulenza-lavoro-welfare",
  "Check up tecnico-amministrativo": "/servizi/check-up-tecnico-amministrativo",
  "Sicurezza sul lavoro": "/servizi/sicurezza-lavoro",
  "Sorveglianza sanitaria": "/servizi/sorveglianza-sanitaria",
  "Sicurezza nei cantieri": "/servizi/sicurezza-cantieri",
  "Indagini Ambientali": "/servizi/indagini-ambientali",
  "Igiene degli alimenti H.A.C.C.P.": "/servizi/igiene-alimenti-haccp",
  "Privacy e GDPR": "/servizi/privacy-gdpr",
  "Certificazioni di Qualità e Sistemi di Gestione": "/servizi/certificazioni-qualita",
  "Certificazione ESG": "/servizi/certificazione-esg",
  "Certificazione Modello 231": "/servizi/modello-231",
  "Business Plan": "/servizi/business-plan",
  "Patente a Crediti Cantieri": "/servizi/patente-crediti-cantieri",
  "Gestione dei rifiuti": "/servizi/gestione-rifiuti",
  "Check-up Digitale & AI per le PMI": "/servizi/check-up-digitale-ai",
  "Finanza Agevolata e Bandi per le Imprese a Roma": "/servizi/finanza-agevolata-bandi",
  "Sportello Innovazione: SPIN EDI": "/servizi/sportello-spin-edi",
};

const CERTIFICAZIONI = [
  "https://www.confcommercioroma.it/wp-content/uploads/2025/06/Certificazioni-Sportelli-di-Confcommercio-Roma1.png",
  "https://www.confcommercioroma.it/wp-content/uploads/2025/06/Certificazioni-Sportelli-di-Confcommercio-Roma2.png",
  "https://www.confcommercioroma.it/wp-content/uploads/2025/06/Certificazioni-Sportelli-di-Confcommercio-Roma3.png",
  "/local-assets/uniter-iso21001-lilla-accredia.webp",
  "https://www.confcommercioroma.it/wp-content/uploads/2025/06/Certificazioni-Sportelli-di-Confcommercio-Roma5.png",
  "https://www.confcommercioroma.it/wp-content/uploads/2025/06/Certificazioni-Sportelli-di-Confcommercio-Roma6.png",
];

const ESIGENZE = [
  { icon: Rocket, label: "Sto aprendo un'attività", target: "quota", color: "#0050B5", image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80&auto=format" },
  { icon: Scale, label: "Ho bisogno di consulenza legale", target: "quota", color: "#003B75", image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=80&auto=format" },
  { icon: GraduationCap, label: "Devo formare i dipendenti", target: "assistenza", color: "#007ACC", image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&q=80&auto=format" },
  { icon: Search, label: "Cerco finanziamenti e bandi", target: "assistenza", color: "#15803D", image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80&auto=format" },
  { icon: Laptop, label: "Voglio digitalizzare l'impresa", target: "quota", color: "#7C3AED", image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80&auto=format" },
  { icon: AlertTriangle, label: "Ho un problema con un dipendente", target: "quota", color: "#C2410C", image: "https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=800&q=80&auto=format" },
  { icon: ShieldCheck, label: "Devo mettermi in regola", target: "assistenza", color: "#0E7490", image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80&auto=format" },
  { icon: Gift, label: "Voglio risparmiare con le convenzioni", target: "convenzioni", color: "#BE123C", image: "https://images.unsplash.com/photo-1556742111-a301076d9d18?w=800&q=80&auto=format" },
];

export default function ServiziPage() {
  const [activeTab, setActiveTab] = useState("quota");
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && ["quota", "assistenza"].includes(hash)) {
      setActiveTab(hash);
      setTimeout(() => {
        document.getElementById("servizi-tabs")?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.hash]);

  const scrollToServizi = (target) => {
    if (target === "convenzioni") {
      window.location.href = "/convenzioni";
      return;
    }
    setActiveTab(target);
    document.getElementById("servizi-tabs")?.scrollIntoView({ behavior: "smooth" });
  };

  const activeCategoria = SERVIZI_CATEGORIE.find((c) => c.id === activeTab);

  return (
    <main id="main-content" data-testid="servizi-page">
      {/* Hero */}
      <section className="relative h-[380px] md:h-[440px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1920&q=60&auto=format" alt="Team di professionisti" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.85)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Per la tua impresa</span>
          <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-[1.1]">
            Tutto ciò di cui la tua impresa ha bisogno
          </h1>
          <p className="text-lg text-white/80 mt-5 leading-relaxed max-w-3xl">
            Consulenza, formazione, assistenza, digitalizzazione. Un sistema completo di servizi pensato per supportare ogni fase della vita della tua attività.
          </p>
          <button onClick={() => document.getElementById("servizi-tabs")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-semibold hover:opacity-90 transition-opacity shadow-lg text-sm mt-8 w-fit">
            Esplora i servizi <ArrowRight size={18} aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* Griglia fotografica interattiva */}
      <section id="esigenze" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Parti da qui</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              In quale situazione ti riconosci?
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {ESIGENZE.map((e, i) => (
              <button
                key={e.label}
                onClick={() => scrollToServizi(e.target)}
                className={`group relative overflow-hidden rounded-2xl ${i === 0 || i === 5 ? "md:col-span-2 md:row-span-2" : ""}`}
                style={{ minHeight: i === 0 || i === 5 ? "320px" : "160px" }}
              >
                <img
                  src={e.image}
                  alt={e.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#041E42]/60 group-hover:bg-[#041E42]/40 transition-colors duration-300" />
                <div className="relative h-full flex flex-col justify-end p-4 md:p-5">
                  <span className="text-white font-semibold text-sm md:text-base font-['Encode_Sans_Semi_Condensed'] leading-snug drop-shadow-lg">{e.label}</span>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-white/0 group-hover:text-white transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    Scopri i servizi <ArrowRight size={14} aria-hidden="true" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs servizi */}
      <section id="servizi-tabs" className="py-20 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">I tre pilastri</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--confcom-text-primary)] mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Il sistema dei servizi
            </h2>
          </div>

          {/* Tab headers */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10 justify-center" role="tablist">
            {SERVIZI_CATEGORIE.map((cat) => {
              const Icon = ICON_MAP[cat.icon] || Shield;
              const isActive = activeTab === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  role="tab"
                  aria-selected={isActive}
                  className={`flex items-center gap-3 px-5 py-3.5 rounded-xl font-semibold text-sm transition-all font-['Encode_Sans_Semi_Condensed'] ${
                    isActive
                      ? "bg-[var(--confcom-blue)] text-white shadow-lg"
                      : "bg-white text-[var(--confcom-text-secondary)] border border-[var(--confcom-border)] hover:border-[var(--confcom-blue)] hover:text-[var(--confcom-blue)]"
                  }`}
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isActive ? "bg-white/20" : "bg-[var(--confcom-bg-blue-light)]"
                  }`}>
                    <Icon size={17} className={isActive ? "text-white" : "text-[var(--confcom-blue)]"} aria-hidden="true" />
                  </span>
                  {cat.titolo}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          {activeCategoria && (
            <div className="bg-white rounded-2xl border border-[var(--confcom-border)] overflow-hidden shadow-sm" role="tabpanel">
              <div className="p-8 md:p-10">
                <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8">{activeCategoria.descrizione}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {activeCategoria.servizi.map((s) => {
                    const link = SERVIZI_LINKS[s.nome];
                    const CardWrapper = link ? Link : "div";
                    const ero = EROGATORI[s.erogatore];
                    const cat = CATEGORIE_SERVIZIO[s.categoria];
                    return (
                      <CardWrapper
                        key={s.nome}
                        {...(link ? { to: link } : {})}
                        className={`flex items-start gap-3 p-5 bg-[var(--confcom-bg-subtle)] rounded-xl border border-transparent hover:border-[var(--confcom-blue)]/30 hover:shadow-md transition-all${link ? " group cursor-pointer" : ""}`}
                      >
                        <div className="flex-1">
                          {(ero || cat) && (
                            <div className="flex items-center gap-1.5 flex-wrap mb-2">
                              {ero && (
                                <span
                                  className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: ero.bg || `${ero.hex}18`, color: ero.hex }}
                                  data-testid={`erogatore-${s.erogatore}`}
                                  title={`Servizio erogato da ${ero.label}`}
                                >
                                  {ero.short}
                                </span>
                              )}
                              {cat && (
                                <span
                                  className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                                  style={{ backgroundColor: cat.bg, color: cat.hex }}
                                  data-testid={`categoria-${s.categoria}`}
                                  title={`Categoria: ${cat.label}`}
                                >
                                  {cat.label}
                                </span>
                              )}
                            </div>
                          )}
                          <h3 className={`text-sm font-semibold font-['Encode_Sans_Semi_Condensed'] mb-1${link ? " text-[var(--confcom-blue)]" : " text-[var(--confcom-text-primary)]"}`}>{s.nome}</h3>
                          <p className="text-xs text-[var(--confcom-text-secondary)] leading-relaxed">{s.desc}</p>
                        </div>
                        {link && <ArrowRight size={14} className="text-[var(--confcom-blue)] shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />}
                      </CardWrapper>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Certificazioni */}
      <section className="pt-8 pb-14 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5 text-center">Le nostre certificazioni</h2>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4 text-center">
            Lavoriamo con e per le imprese. I punti di forza delle nostre business-unit sono la formazione e i servizi: è grazie a questi che aiutiamo le aziende a crescere e migliorare le proprie performance. In più, attraverso una ricerca costante e capillare, proponiamo finanziamenti istituzionali e privati e strumenti che soddisfino ogni necessità.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-8 text-center">
            Le certificazioni riflettono il nostro impegno costante nell'offrire un servizio impeccabile, garantendo standard elevati in ogni aspetto del nostro lavoro. Qualità, sicurezza, ambiente e inclusività sono valori fondamentali che ci permettono di soddisfare le esigenze dei clienti con efficienza, affidabilità e competenza.
          </p>
          <div className="grid grid-cols-4 md:grid-cols-7 gap-4">
            {CERTIFICAZIONI.map((src, i) => (
              <div key={i} className="bg-white rounded-xl p-3 shadow-sm border border-[var(--confcom-border)] aspect-square flex items-center justify-center">
                <img src={src} alt={`Certificazione ${i + 1}`} className="w-full h-full object-contain" loading="lazy" />
              </div>
            ))}
            <div className="bg-white rounded-xl p-3 shadow-sm border border-[var(--confcom-border)] aspect-square flex items-center justify-center">
              <img src="https://www.confcommercioroma.it/wp-content/uploads/2026/03/LOGO_IMPRESA_ROSA_COL-1-300x238.jpg" alt="Impresa Rosa Regione Lazio" className="w-full h-full object-contain" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Banner Promo.Ter */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--confcom-blue)] rounded-2xl px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] mb-1">Promo.Ter: formazione e servizi per le imprese</p>
              <p className="text-white/75 text-sm">Scopri tutti i servizi di Promo.Ter, la società di Confcommercio Roma al fianco delle imprese ogni giorno.</p>
            </div>
            <Link to="/chi-siamo/enti-collegati/promo-ter" className="shrink-0 inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-6 py-3 rounded-md font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
              Vai a Promo.Ter <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/cta-background.jpg" alt="" className="w-full h-full object-cover" aria-hidden="true" />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.90)" }} />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-4">
            Non sei ancora associato?
          </h2>
          <p className="text-lg text-white/80 mb-8">Scopri tutti i vantaggi dell'adesione e inizia subito a far crescere la tua impresa con il supporto di Confcommercio Roma.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/associati-ora" className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-8 py-4 rounded-md font-bold hover:opacity-90 transition-opacity shadow-lg">
              Associati ora <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <a href={`tel:${SITE_INFO.greenNumber.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-md font-semibold hover:opacity-90 transition-opacity">
              <Phone size={18} aria-hidden="true" /> {SITE_INFO.greenNumber}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
